import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { API_BASE, DEFAULT_HEADERS } from "../../services/apiClient";
import {
  getCachedUserBundle,
  getCurrentUserId,
} from "../../services/authService";
import { getLocalizedNewsTitle, getLocalizedField } from "../../utils/newsLocalization";

// Newest N of each content type, per type, per load.
//
// These four endpoints return the whole table when no limit is given, and the
// news table alone is a few thousand rows — so every dashboard load was
// fetching several thousand rows across four tables, attaching like counts to
// all of them and shipping the lot as JSON, in order to show twenty items.
// That was the platform's slowest request by a wide margin, most of its
// outbound bandwidth, and the reason memory sat near its ceiling.
//
// The feed shows twenty at a time and loads twenty more on demand, so fifty of
// each is more than the first pages can display. The landing page already does
// this; the dashboard never did.
const CONTENT_LIMIT = 50;

const isProd = process.env.NODE_ENV === "production";
const debugLog = (...args) => {
  if (!isProd) {
    // eslint-disable-next-line no-console
    console.log("[DashboardFeed]", ...args);
  }
};

export const useDashboardFeed = ({
  profile,
  user,
  locationPathname,
  onHydrateLikes,
  refreshKey = 0,
}) => {
  const [posts, setPosts] = useState([]);
  const [conferences, setConferences] = useState([]);
  const [combinedPosts, setCombinedPosts] = useState([]);
  const [myPostsCount, setMyPostsCount] = useState(0);
  const [myArticlesCount, setMyArticlesCount] = useState(0);
  const { i18n } = useTranslation("common");
  const currentLanguage = (i18n.language || "en").split("-")[0];

  useEffect(() => {
    if (!locationPathname || !locationPathname.startsWith("/dashboard")) {
      return;
    }

    let cancelled = false;
    const fetchData = async () => {
      try {
        const token =
          localStorage.getItem("authToken") ||
          sessionStorage.getItem("authToken");
        let userIdHint = getCurrentUserId();
        if (!userIdHint) {
          try {
            const cached =
              localStorage.getItem("userprofile") ||
              sessionStorage.getItem("userprofile");
            if (cached) {
              const j = JSON.parse(cached);
              userIdHint =
                j?.user?.id ||
                j?.profile?.user?.id ||
                j?.profile?.id ||
                j?.id ||
                null;
            }
          } catch (_) {}
        }

        const commonHeaders = {
          headers: {
            "X-API-KEY": "f3bb4afa55070cc052d1b888d4f7465a",
          },
        };
        const postsFetchOptions = {
          method: "GET",
          headers: {
            Accept: "application/json",
            ...DEFAULT_HEADERS,
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(userIdHint ? { "X-User-Id": String(userIdHint) } : {}),
          },
          credentials: "include",
        };
        const userPostsUrl = userIdHint
          ? `${API_BASE}/feed/users/${encodeURIComponent(
              userIdHint
            )}/posts?limit=200`
          : null;

        const articlesUrl = `${API_BASE}/articles/documents${
          userIdHint ? `?user_id=${encodeURIComponent(userIdHint)}` : ""
        }`;

        const [
          postsRes,
          newsRes,
          confRes,
          pubRes,
          videoRes,
          articlesRes,
          myPostsRes,
          noticesRes,
        ] = await Promise.all([
          fetch(`${API_BASE}/feed/posts?limit=50&offset=0`, postsFetchOptions),
          fetch(`${API_BASE}/wineries/news?limit=${CONTENT_LIMIT}`, commonHeaders),
          fetch(`${API_BASE}/wineries/conferences?limit=${CONTENT_LIMIT}`, commonHeaders),
          fetch(`${API_BASE}/wineries/publications?limit=${CONTENT_LIMIT}`, commonHeaders),
          fetch(`${API_BASE}/wineries/videos?limit=${CONTENT_LIMIT}`, commonHeaders),
          fetch(articlesUrl, postsFetchOptions),
          userPostsUrl ? fetch(userPostsUrl, postsFetchOptions) : null,
          fetch(`${API_BASE}/notices?status=active`, commonHeaders),
        ]);

        const safeJson = async (res, label) => {
          try {
            if (!res) return [];
            if (!res.ok) {
              const errText = await res.text().catch(() => "");
              console.warn(
                `[Dashboard] ${label || "fetch"} failed ${
                  res.status
                }: ${errText}`
              );
              return [];
            }
            return await res.json();
          } catch (_) {
            return [];
          }
        };

        const normalizeArray = (x) => {
          if (Array.isArray(x)) return x;
          if (x && Array.isArray(x.results)) return x.results;
          if (x && Array.isArray(x.data)) return x.data;
          if (x && typeof x === "object") {
            if (Array.isArray(x.items)) return x.items;
            if (Array.isArray(x.rows)) return x.rows;
          }
          return [];
        };

        const rawPosts = await safeJson(postsRes, "posts");
        const postsArr = normalizeArray(rawPosts);
        const rawUserPosts = await safeJson(myPostsRes, "user posts");
        const userPostsArr = normalizeArray(rawUserPosts);
        const rawArticles = await safeJson(articlesRes, "articles");
        const articleArr = normalizeArray(rawArticles);

        const summarize = (arr) =>
          (arr || []).slice(0, 5).map((p) => ({
            id:
              p?.id ||
              p?.post_id ||
              p?.item_id ||
              p?.uuid ||
              p?._id ||
              p?.guid ||
              null,
            type: p?.type,
            like_count: p?.like_count,
            liked_by_me: p?.liked_by_me,
            likers: Array.isArray(p?.likers) ? p.likers.length : 0,
            user_id:
              p?.user_id ||
              p?.userId ||
              p?.user?.id ||
              p?.author_id ||
              p?.owner_id ||
              null,
          }));
        debugLog("posts fetched", {
          total: postsArr.length,
          sample: summarize(postsArr),
        });
        debugLog("user posts fetched", {
          total: userPostsArr.length,
          sample: summarize(userPostsArr),
        });

        const articleDocs = (articleArr || []).filter((doc) => {
          if (!doc) return false;
          const status = String(doc.status || "").toLowerCase();
          if (!status) return true;
          return !["pre-signed", "pending", "draft"].includes(status);
        });

        const articleAuthorMap = new Map();
        const addAuthorInfo = (uid, info) => {
          if (!uid) return;
          const key = String(uid);
          if (articleAuthorMap.has(key)) return;
          articleAuthorMap.set(key, {
            first_name: info?.first_name || "",
            last_name: info?.last_name || "",
            avatar_url: info?.avatar_url || "",
          });
        };

        const cachedBundle = getCachedUserBundle();
        const cachedProfile = cachedBundle?.profile || null;
        const cachedUser = cachedBundle?.user || null;
        const localProfile = profile || cachedProfile || null;
        const localUser = user || cachedUser || null;
        const localUidCandidates = [
          localProfile?.user_id,
          localProfile?.id,
          localUser?.id,
        ].filter(Boolean);
        for (const cid of localUidCandidates) {
          addAuthorInfo(cid, {
            first_name:
              localProfile?.first_name ||
              localUser?.first_name ||
              cachedBundle?.first_name ||
              "",
            last_name:
              localProfile?.last_name ||
              localUser?.last_name ||
              cachedBundle?.last_name ||
              "",
            avatar_url: localProfile?.avatar_url || localUser?.avatar_url || "",
          });
        }

        const articleUserIds = Array.from(
          new Set(
            (articleDocs || [])
              .map((doc) => doc?.user_id || doc?.userId || null)
              .filter(Boolean)
          )
        );

        const profileFetchOptions = {
          method: "GET",
          headers: postsFetchOptions.headers,
          credentials: "include",
        };

        const unresolvedAuthorIds = articleUserIds.filter(
          (uid) => !articleAuthorMap.has(String(uid))
        );

        if (unresolvedAuthorIds.length > 0) {
          await Promise.all(
            unresolvedAuthorIds.map(async (uid) => {
              try {
                const res = await fetch(
                  `${API_BASE}/wineries/profile?user_id=${encodeURIComponent(
                    uid
                  )}`,
                  profileFetchOptions
                );
                if (!res.ok) return;
                const data = await res.json().catch(() => ({}));
                const prof = data?.profile || data?.data || data;
                if (prof) {
                  addAuthorInfo(uid, {
                    first_name: prof.first_name || "",
                    last_name: prof.last_name || "",
                    avatar_url: prof.avatar_url || "",
                  });
                }
              } catch (err) {
                console.warn("[Dashboard] profile fetch failed", uid, err);
              }
            })
          );
        }

        const newsData = await safeJson(newsRes, "news");
        const confData = await safeJson(confRes, "conferences");
        const pubData = await safeJson(pubRes, "publications");
        const videoData = await safeJson(videoRes, "videos");
        const noticeData = normalizeArray(await safeJson(noticesRes, "notices"));

        const enhancedNews = newsData.map((item) => {
          const localizedTitle = getLocalizedNewsTitle(item, currentLanguage);
          return {
            ...item,
            type: "news",
            like_count: Number.isFinite(item.like_count)
              ? item.like_count
              : item.likes || 0,
            liked_by_me: !!item.liked_by_me,
            likers: Array.isArray(item.likers) ? item.likers : [],
            title: localizedTitle,
            date_created:
              item.date_created ||
              item.pub_date ||
              item.date ||
              item.created_at ||
              new Date().toISOString(),
          };
        });
        const enhancedConf = confData.map((item) => ({
          ...item,
          type: "conference",
          like_count: Number.isFinite(item.like_count)
            ? item.like_count
            : item.likes || 0,
          liked_by_me: !!item.liked_by_me,
          likers: Array.isArray(item.likers) ? item.likers : [],
          date_created:
            item.date_created ||
            item.date ||
            item.created_at ||
            new Date().toISOString(),
          image: `/conference_${Math.floor(Math.random() * 20) + 1}.png`,
        }));

        const enhancedPubs = pubData.map((item) => ({
          ...item,
          type: "publication",
          like_count: Number.isFinite(item.like_count)
            ? item.like_count
            : item.likes || 0,
          liked_by_me: !!item.liked_by_me,
          likers: Array.isArray(item.likers) ? item.likers : [],
          title: getLocalizedField(item, "title", currentLanguage),
          insights: getLocalizedField(item, "insights", currentLanguage),
          abstract: getLocalizedField(item, "abstract", currentLanguage),
          date_created:
            item.date_created ||
            item.pub_date ||
            item.created_at ||
            new Date().toISOString(),
        }));

        const enhancedVideos = videoData.map((item) => ({
          ...item,
          type: "video",
          like_count: Number.isFinite(item.like_count)
            ? item.like_count
            : item.likes || 0,
          liked_by_me: !!item.liked_by_me,
          likers: Array.isArray(item.likers) ? item.likers : [],
          date_created: item.published_at || new Date().toISOString(),
          channel_title: item.channel_title || item.source || "YouTube",
          thumbnail_url:
            item.channel_thumbnail_url ||
            item.thumbnail_url ||
            item.image ||
            null,
          link:
            item.url || item.link || item.youtube_url || item.video_url || null,
          description: item.description || item.summary || "",
        }));

        // Notices are calls to action rather than articles: no likes, and an
        // expiry that decides whether they still belong in the feed at all.
        const now = Date.now();
        const enhancedNotices = noticeData
          .filter((item) => {
            if (!item?.expires_on) return true;
            const expiry = new Date(item.expires_on).getTime();
            return isNaN(expiry) ? true : expiry >= now;
          })
          .map((item) => ({
            ...item,
            type: "notice",
            id: item.notice_id || item.id,
            title: item.title || "",
            description: item.description || item.content || "",
            // The notices table names these date_created / last_updated.
            date_created:
              item.date_created ||
              item.created_at ||
              item.last_updated ||
              new Date().toISOString(),
          }));

        const normalizePost = (p, forceUserId = null, authorProfile = null) => {
          const rawId =
            p.id ||
            p.post_id ||
            p.item_id ||
            p.uuid ||
            p._id ||
            p.guid ||
            null;
          const rawUserId =
            p.user_id ||
            p.userId ||
            p.user?.id ||
            // What /feed/posts calls the author's id.
            p.author_user_id ||
            p.author?.id ||
            p.author_id ||
            p.owner_id ||
            null;
          const isMine =
            forceUserId &&
            (rawUserId == null ||
              String(rawUserId) === String(forceUserId) ||
              String(p.user_id) === String(forceUserId));
          const assignedUserId = isMine
            ? forceUserId
            : rawUserId != null
            ? rawUserId
            : null;
          return {
            id: rawId || p.url || p.link || p.permalink || p.href || null,
            type: "post",
            user_id: assignedUserId,
            body: p.body ?? "",
            title: p.title || "",
            post_subtype: p.post_subtype || p.subtype || null,
            // Set when this post announces a paper; used to keep that paper
            // from also appearing as a card of its own.
            document_id: p.document_id ?? p.documentId ?? null,
            // The announced paper, joined server-side, so the card renders for
            // every reader rather than only for its author.
            document: p.document || null,
            theme: p.theme || p.project_theme || "",
            regional_cluster: p.regional_cluster || p.region || "",
            project_scope: p.project_scope || p.scope || "",
            project_track: p.project_track || p.track || "",
            routing_target: p.routing_target || p.routing || "",
            created_at:
              p.created_at || p.updated_at || new Date().toISOString(),
            date_created:
              p.date_created ||
              p.created_at ||
              p.updated_at ||
              new Date().toISOString(),
            media_url: p.media_url || p.image_url || p.video_url || null,
            media_type:
              p.media_type ||
              (p.image_url ? "image" : p.video_url ? "video" : null) ||
              "none",
            author_first_name:
              p.author_first_name ||
              (isMine ? authorProfile?.first_name : "") ||
              "",
            author_last_name:
              p.author_last_name ||
              (isMine ? authorProfile?.last_name : "") ||
              "",
            author_avatar_url:
              p.author_avatar_url ||
              (isMine ? authorProfile?.avatar_url : "") ||
              "",
            // The author's address, so their name in the feed is a way to
            // their profile and not just a label.
            author_public_id:
              p.author_public_id || p.author?.public_id || p.user?.public_id || null,
            author_profile_slug:
              p.author_profile_slug || p.author?.profile_slug || null,
            liked_by_me: !!p.liked_by_me,
            likers: Array.isArray(p.likers) ? p.likers : [],
            like_count: Number.isFinite(p.like_count)
              ? p.like_count
              : p.likes || 0,
            comment_count: Number.isFinite(p.comment_count)
              ? p.comment_count
              : p.comments || 0,
            share_count: Number.isFinite(p.share_count)
              ? p.share_count
              : p.shares || 0,
            scope: p.scope || null,
            team_key: p.team_key || null,
          };
        };

        const enhancedUserPosts = (postsArr || []).map((p) =>
          normalizePost(p, null, profile)
        );

        const myPostsNormalized =
          userIdHint && Array.isArray(userPostsArr)
            ? userPostsArr.map((p) => normalizePost(p, userIdHint, profile))
            : [];

        const uploadedArticlePosts = (articleDocs || []).map((doc) => {
          const authorKey = doc?.user_id || doc?.userId || null;
          const authorInfo = articleAuthorMap.get(String(authorKey)) || {};
          const uploadedAt =
            doc?.uploaded_at ||
            doc?.uploadedAt ||
            doc?.updated_at ||
            doc?.updatedAt ||
            doc?.created_at ||
            doc?.createdAt ||
            new Date().toISOString();
          const downloadUrl =
            doc?.public_url || doc?.source_url || doc?.url || null;
          return {
            id: `uploaded-article-${
              doc?.id ||
              doc?.document_id ||
              doc?.documentId ||
              doc?.storage_path ||
              doc?.path ||
              Math.random().toString(36).slice(2)
            }`,
            type: "post",
            user_id: doc?.user_id || doc?.userId || null,
            post_subtype: "uploaded_article",
            title: doc?.title || doc?.storage_path || "Uploaded article",
            date_created: uploadedAt,
            created_at: uploadedAt,
            media_url: doc?.thumbnail_url || null,
            media_type: doc?.thumbnail_url ? "image" : "none",
            author_first_name: authorInfo.first_name || "",
            author_last_name: authorInfo.last_name || "",
            author_avatar_url: authorInfo.avatar_url || "",
            like_count: Number.isFinite(doc?.like_count)
              ? doc.like_count
              : doc?.likes || 0,
            comment_count: 0,
            share_count: 0,
            liked_by_me: !!doc?.liked_by_me,
            likers: Array.isArray(doc?.likers) ? doc.likers : [],
            abstract: doc?.abstract || "",
            insight: doc?.insight || "",
            download_url: downloadUrl,
            document_id: doc?.id ?? doc?.document_id ?? doc?.documentId ?? null,
            document: doc,
          };
        });

        const getDisplayedDate = (it) => {
          if (!it) return new Date(0);
          let raw = null;
          switch (it.type) {
            case "conference":
              raw =
                it.date_conference ||
                it.date ||
                it.date_created ||
                it.created_at ||
                it.updated_at ||
                null;
              break;
            case "publication":
              raw =
                it.date_created ||
                it.date_published ||
                it.pub_date ||
                it.created_at ||
                it.updated_at ||
                null;
              break;
            case "video":
              raw =
                it.date_created ||
                it.published_at ||
                it.pub_date ||
                it.created_at ||
                it.updated_at ||
                null;
              break;
            case "news":
              raw =
                it.date_created ||
                it.pub_date ||
                it.date ||
                it.created_at ||
                it.updated_at ||
                null;
              break;
            case "post":
            default:
              raw = it.date_created || it.created_at || it.updated_at || null;
              break;
          }
          const d = raw ? new Date(raw) : null;
          return isNaN(d?.getTime?.()) ? new Date(0) : d;
        };

        const newsSorted = [...enhancedNews].sort(
          (a, b) => getDisplayedDate(b) - getDisplayedDate(a)
        );
        const confSorted = [...enhancedConf].sort(
          (a, b) => getDisplayedDate(b) - getDisplayedDate(a)
        );
        const pubsSorted = [...enhancedPubs].sort(
          (a, b) => getDisplayedDate(b) - getDisplayedDate(a)
        );
        const videosSorted = [...enhancedVideos].sort(
          (a, b) => getDisplayedDate(b) - getDisplayedDate(a)
        );
        const noticesSorted = [...enhancedNotices].sort(
          (a, b) => getDisplayedDate(b) - getDisplayedDate(a)
        );

        const myPostsCountDerived =
          (userPostsArr || []).length > 0
            ? userPostsArr.length
            : (postsArr || []).filter((p) => {
                const owner =
                  p?.user_id ||
                  p?.userId ||
                  p?.user?.id ||
                  p?.author_id ||
                  p?.owner_id ||
                  null;
                if (!owner || !userIdHint) return false;
                return String(owner) === String(userIdHint);
              }).length;
        const myArticlesCountDerived = (articleDocs || []).length;

        const postsById = new Map();
        const upsertPost = (p) => {
          if (!p || !p.id) return;
          const existing = postsById.get(p.id);
          if (!existing) {
            postsById.set(p.id, p);
            return;
          }
          // Prefer the version that has a user_id (so deletes work)
          if (existing.user_id == null && p.user_id != null) {
            postsById.set(p.id, p);
          }
        };

        enhancedUserPosts.forEach(upsertPost);
        myPostsNormalized.forEach(upsertPost);

        // Adding a paper writes two rows: the paper, and a post announcing
        // it. Both reach this hook — the post from /feed/posts, the paper
        // from /articles/documents — and until each post carried the id of
        // the paper it announces, there was no way to tell they were the same
        // thing, so every new paper appeared twice. The announcement is what
        // everyone else sees, so it is the one that stays; the card is kept
        // only for papers that have no announcement (added before this, or
        // whose post was deleted).
        const announcedDocIds = new Set(
          [...postsById.values()]
            .map((p) => p?.document_id)
            .filter((id) => id !== null && id !== undefined)
            .map(String)
        );
        const unannouncedArticles = uploadedArticlePosts.filter(
          (item) =>
            item.document_id === null ||
            item.document_id === undefined ||
            !announcedDocIds.has(String(item.document_id))
        );

        const allPostItems = [...postsById.values(), ...unannouncedArticles];
        const postsSorted = [...allPostItems].sort(
          (a, b) => getDisplayedDate(b) - getDisplayedDate(a)
        );

        const interleaveByBucket = (...buckets) => {
          const lists = buckets.filter(Array.isArray);
          const total = lists.reduce((sum, list) => sum + list.length, 0);
          if (total === 0) return [];
          const indices = lists.map(() => 0);
          const result = [];
          let i = 0;
          while (result.length < total) {
            const bucketIdx = i % lists.length;
            const bucket = lists[bucketIdx];
            if (indices[bucketIdx] < bucket.length) {
              result.push(bucket[indices[bucketIdx]]);
              indices[bucketIdx] += 1;
            }
            i += 1;
          }
          return result;
        };

        let merged = interleaveByBucket(
          postsSorted,
          newsSorted,
          confSorted,
          pubsSorted,
          videosSorted,
          noticesSorted
        );

        if (merged.length === 0) {
          merged = [];
        }

        if (cancelled) return;
        setPosts(postsSorted);
        setConferences(confSorted);
        setCombinedPosts(merged);
        setMyPostsCount(myPostsCountDerived);
        setMyArticlesCount(myArticlesCountDerived);

        if (typeof onHydrateLikes === "function") {
          onHydrateLikes(merged, setCombinedPosts);
        }
      } catch (error) {
        console.error("Error fetching posts:", error);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [locationPathname, onHydrateLikes, profile, user, currentLanguage, refreshKey]);

  return {
    posts,
    conferences,
    combinedPosts,
    setCombinedPosts,
    myPostsCount,
    setMyPostsCount,
    myArticlesCount,
  };
};
